import { stringSimilarity } from 'string-similarity-js'
import type { Services } from './types'
import type { Storage } from '~/storage/types'
import type {
    BankTransaction,
    DocumentTransactionAssociation,
    Invoice,
} from '~/storage/types'
import { convertCurrency } from '~common/utils/conversion-rates/currency-converter.ts'
interface IndividualMatchScore {
    vector: 'date' | 'amount' | 'vendor' | 'text'
    score: number
    weight: number
    details: string
}

interface MatchScoreResult {
    confidence: number
    individualScores: IndividualMatchScore[]
}

const MATCHING_CONFIG = {
    MAX_DAYS_WINDOW: 30,
    MIN_CONFIDENCE_FOR_SUGGESTION: 60,
    MAX_SUGGESTIONS_PER_ITEM: 5,
    WEIGHTS: {
        DATE_WEIGHT: 30,
        AMOUNT_WEIGHT: 40,
        VENDOR_WEIGHT: 15,
        TEXT_WEIGHT: 15,
    },
    DATE: {
        MIN_SCORE: 0,
        PENALTY_PER_DAY: 5,
        PENALTY_FOR_CLOSE_DAYS: 2.5, // half for <=1 day
    },
    AMOUNT: {
        MIN_SCORE: 0,
        MAX_DIFF_PERCENT: 25,
        PENALTY_PER_5_PERCENT: 20,
    },
    VENDOR: {
        MATCH_SCORE: 100,
        NO_MATCH_SCORE: 0,
        SIMILARITY_THRESHOLD: 85,
    },
    TEXT: {
        SIMILARITY_THRESHOLD: 50, // 0.5 * 100
        NO_MATCH_SCORE: 0,
        WORD_BONUS_PER_MATCH: 30,
    },
} as const

function calculateDateDifference(date1: Date, date2: Date): number {
    const timeDiff = Math.abs(date1.getTime() - date2.getTime())
    return Math.ceil(timeDiff / (1000 * 3600 * 24))
}

function calculateAmountDifference(amount1: number, amount2: number): number {
    if (amount1 === 0 && amount2 === 0) return 0
    if (amount1 === 0 || amount2 === 0) return 100

    const absAmount1 = Math.abs(amount1)
    const absAmount2 = Math.abs(amount2)
    const biggerValue = Math.max(absAmount1, absAmount2)
    const baseLinePercent = biggerValue / 100
    const difference = Math.abs(absAmount1 - absAmount2)
    const percentageDifference = difference / baseLinePercent || 0
    return percentageDifference
}

function isExactAmountMatch(
    transactionAmount: number,
    invoiceAmount: number,
): boolean {
    return (
        Math.abs(transactionAmount) === Math.abs(invoiceAmount) &&
        transactionAmount * invoiceAmount < 0
    ) // opposite signs
}

function calculateTextSimilarity(
    transactionText: string,
    invoiceText: string,
): number {
    if (!transactionText || !invoiceText) {
        return 0
    }

    // Clean and normalize text for better matching
    // Replace special characters and punctuation with spaces
    const cleanTransactionText = transactionText
        .toLowerCase()
        .replace(/[^\w\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()

    const cleanInvoiceText = invoiceText
        .toLowerCase()
        .replace(/[^\w\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()

    if (!cleanTransactionText || !cleanInvoiceText) {
        return 0
    }

    // Calculate base similarity using the library
    const baseSimilarity = stringSimilarity(
        cleanTransactionText,
        cleanInvoiceText,
    )

    // Convert from 0-1 scale to 0-100 scale
    let finalSimilarity = baseSimilarity * 100

    // Add bonus for exact word matches
    const transactionWords = new Set(
        cleanTransactionText.split(' ').filter((word) => word.length > 0),
    )

    const invoiceWords = new Set(
        cleanInvoiceText.split(' ').filter((word) => word.length > 0),
    )

    // Count exact word matches
    let exactMatches = 0
    for (const word of transactionWords) {
        if (invoiceWords.has(word)) {
            exactMatches++
        }
    }

    // Add 0.3 to similarity for each exact word match
    const wordMatchBonus = exactMatches * 30
    finalSimilarity += wordMatchBonus

    // Cap the similarity at 100
    finalSimilarity = Math.min(finalSimilarity, 100)

    return finalSimilarity
}

function calculateVectorScore(
    vector: 'date' | 'amount' | 'vendor' | 'text',
    transaction: BankTransaction,
    invoice: Invoice,
): IndividualMatchScore {
    const metadata = invoice.metadata
    const weights = MATCHING_CONFIG.WEIGHTS

    switch (vector) {
        case 'date': {
            if (!transaction.date || !metadata.date) {
                return {
                    vector: 'date',
                    score: 0,
                    weight: weights.DATE_WEIGHT,
                    details: 'ignore',
                }
            }

            const txDate = new Date(transaction.date)
            const invDate = new Date(metadata.date)
            if (isNaN(txDate.getTime()) || isNaN(invDate.getTime())) {
                return {
                    vector: 'date',
                    score: 0,
                    weight: weights.DATE_WEIGHT,
                    details: 'ignore',
                }
            }

            if (txDate.toDateString() === invDate.toDateString()) {
                return {
                    vector: 'date',
                    score: 100,
                    weight: weights.DATE_WEIGHT,
                    details: 'Exact date match',
                }
            }

            const daysDiff = calculateDateDifference(txDate, invDate)
            const maxDays = MATCHING_CONFIG.MAX_DAYS_WINDOW

            if (daysDiff > maxDays) {
                return {
                    vector: 'date',
                    score: MATCHING_CONFIG.DATE.MIN_SCORE,
                    weight: weights.DATE_WEIGHT,
                    details: `${daysDiff} days difference, outside window`,
                }
            }

            const penaltyPerDay =
                daysDiff <= 3
                    ? MATCHING_CONFIG.DATE.PENALTY_FOR_CLOSE_DAYS
                    : MATCHING_CONFIG.DATE.PENALTY_PER_DAY
            const penalty = daysDiff * penaltyPerDay
            const score = Math.max(
                MATCHING_CONFIG.DATE.MIN_SCORE,
                100 - penalty,
            )

            return {
                vector: 'date',
                score,
                weight: weights.DATE_WEIGHT,
                details: `${daysDiff} days difference`,
            }
        }

        case 'amount': {
            if (!transaction.amount || !metadata.amount) {
                return {
                    vector: 'amount',
                    score: 0,
                    weight: weights.AMOUNT_WEIGHT,
                    details: 'ignore',
                }
            }

            let normalizedInvoiceAmount = metadata.amount
            const transactionCurrency = transaction.currency
            const invoiceCurrency = metadata.currency

            if (
                transactionCurrency &&
                invoiceCurrency &&
                transactionCurrency.toUpperCase() !==
                    invoiceCurrency.toUpperCase()
            ) {
                try {
                    const transactionDate = new Date(transaction.date)
                    normalizedInvoiceAmount = convertCurrency(
                        metadata.amount,
                        invoiceCurrency,
                        transactionCurrency,
                        transactionDate,
                    )
                } catch (error) {
                    return {
                        vector: 'amount',
                        score: 0,
                        weight: weights.AMOUNT_WEIGHT,
                        details: `Currency conversion failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
                    }
                }
            }

            const isExact = isExactAmountMatch(
                transaction.amount,
                normalizedInvoiceAmount,
            )
            if (isExact) {
                return {
                    vector: 'amount',
                    score: 100,
                    weight: weights.AMOUNT_WEIGHT,
                    details: 'Exact amount match',
                }
            }

            const percentDiff = calculateAmountDifference(
                transaction.amount,
                normalizedInvoiceAmount,
            )
            if (percentDiff > MATCHING_CONFIG.AMOUNT.MAX_DIFF_PERCENT) {
                return {
                    vector: 'amount',
                    score: MATCHING_CONFIG.AMOUNT.MIN_SCORE,
                    weight: weights.AMOUNT_WEIGHT,
                    details: 'Amounts differ too much',
                }
            }

            const penalty =
                (percentDiff / 5) * MATCHING_CONFIG.AMOUNT.PENALTY_PER_5_PERCENT
            const score = Math.max(
                MATCHING_CONFIG.AMOUNT.MIN_SCORE,
                100 - penalty,
            )

            return {
                vector: 'amount',
                score,
                weight: weights.AMOUNT_WEIGHT,
                details: `Amount difference: ${percentDiff.toFixed(1)}%`,
            }
        }

        case 'vendor': {
            const hasVendorMatch =
                transaction.vendorId &&
                invoice.vendorId &&
                transaction.vendorId === invoice.vendorId
            let score: number = hasVendorMatch
                ? MATCHING_CONFIG.VENDOR.MATCH_SCORE
                : MATCHING_CONFIG.VENDOR.NO_MATCH_SCORE
            const details = hasVendorMatch
                ? 'Vendor ID match'
                : 'No vendor match'

            const vendorName = transaction.vendorName
            const shopName = metadata.vendorName || ''
            const similarity = calculateTextSimilarity(vendorName, shopName)
            if (similarity > MATCHING_CONFIG.VENDOR.SIMILARITY_THRESHOLD) {
                score = Math.max(score, similarity)
            }

            return {
                vector: 'vendor',
                score,
                weight: weights.VENDOR_WEIGHT,
                details: `${details}, name similarity: ${similarity.toFixed(1)}%`,
            }
        }

        case 'text': {
            const transactionText = [transaction.referenceText]
                .filter(Boolean)
                .join(' ')
            const invoiceText = [metadata.reason || '']
                .filter(Boolean)
                .join(' ')

            if (!transactionText || !invoiceText) {
                return {
                    vector: 'text',
                    score: 0,
                    weight: weights.TEXT_WEIGHT,
                    details: 'ignore',
                }
            }

            const similarity = calculateTextSimilarity(
                transactionText,
                invoiceText,
            )
            const bounded = Math.min(100, similarity * 2) // as in prototype *2 ?

            if (similarity >= MATCHING_CONFIG.TEXT.SIMILARITY_THRESHOLD) {
                return {
                    vector: 'text',
                    score: bounded,
                    weight: weights.TEXT_WEIGHT,
                    details: `Text similarity: ${bounded.toFixed(1)}%`,
                }
            } else {
                return {
                    vector: 'text',
                    score: MATCHING_CONFIG.TEXT.NO_MATCH_SCORE,
                    weight: weights.TEXT_WEIGHT,
                    details: `Text similarity below threshold: ${bounded.toFixed(1)}%`,
                }
            }
        }

        default:
            throw new Error(`Unknown vector: ${vector}`)
    }
}

function calculateOverallConfidence(
    individualScores: IndividualMatchScore[],
): number {
    const validScores = individualScores.filter((s) => s.details !== 'ignore')
    const amountScore = validScores.find((s) => s.vector === 'amount')
    const dateScore = validScores.find((s) => s.vector === 'date')

    if (amountScore?.score === 0) return 0
    if (amountScore?.score === 100 && dateScore?.score === 100) return 99

    let weightedSum = 0
    let totalWeight = 0
    for (const score of validScores) {
        const baseWeight =
            MATCHING_CONFIG.WEIGHTS[
                `${score.vector.toUpperCase()}_WEIGHT` as keyof typeof MATCHING_CONFIG.WEIGHTS
            ]
        const effectiveWeight =
            score.score === 100 ? baseWeight * 2 : baseWeight
        weightedSum += score.score * effectiveWeight
        totalWeight += effectiveWeight
    }

    if (totalWeight === 0) return 0
    return Math.min(99, Math.max(0, weightedSum / totalWeight))
}

export class TransactionInvoiceMatchingService {
    constructor(
        private deps: {
            storage: Storage
            getServices: () => Pick<
                Services,
                'cache' | 'errorTracker' | 'bankTransaction'
            >
        },
    ) {}

    private async getCurrentUserId(): Promise<string | null> {
        return (
            (await this.deps.storage.modules.auth.getCurrentUser())?.id || null
        ) // assume method exists
    }

    private computeMatchScore(
        transaction: BankTransaction,
        invoice: Invoice,
    ): MatchScoreResult {
        const individualScores = [
            calculateVectorScore('date', transaction, invoice),
            calculateVectorScore('amount', transaction, invoice),
            calculateVectorScore('vendor', transaction, invoice),
            calculateVectorScore('text', transaction, invoice),
        ] as IndividualMatchScore[]

        const confidence = calculateOverallConfidence(individualScores)

        return { confidence, individualScores }
    }

    private async _findMatches(
        primaryType: 'transaction' | 'invoice',
        primaryId: string,
        hardRefresh = false,
    ): Promise<DocumentTransactionAssociation[]> {
        let primaryItem: BankTransaction | Invoice | null = null
        let isTransactionPrimary = primaryType === 'transaction'
        let candidates: Invoice[] | BankTransaction[]

        if (isTransactionPrimary) {
            primaryItem =
                await this.deps.storage.modules.bankTransaction.getTransactionById(
                    primaryId,
                )
            if (!primaryItem) return []
            candidates = Object.values(
                this.deps.getServices().cache.getKey('invoices') || {},
            ) as Invoice[]
        } else {
            primaryItem =
                await this.deps.storage.modules.emailInvoice.getDocumentById(
                    primaryId,
                )
            if (!primaryItem) return []
            candidates = Object.values(
                this.deps.getServices().cache.getKey('transactions') || {},
            ) as BankTransaction[]
        }

        if (hardRefresh) {
            const associationsToClear = isTransactionPrimary
                ? await this.deps.storage.modules.matchingAssociations.getAssociationsForTransaction(
                      primaryId,
                  )
                : await this.deps.storage.modules.matchingAssociations.getAssociationsForDocument(
                      primaryId,
                  )

            if (isTransactionPrimary) {
                await this.deps.storage.modules.matchingAssociations.clearForTransaction(
                    primaryId,
                )
            } else {
                await this.deps.storage.modules.matchingAssociations.clearForDocument(
                    primaryId,
                )
            }

            if (associationsToClear.length > 0) {
                const cache = this.deps.getServices().cache
                const associationIds = associationsToClear.map((a) => a.id)
                cache.removeDocumentTransactionAssociations(associationIds)
            }
        }

        const existingAssocs = isTransactionPrimary
            ? await this.deps.storage.modules.matchingAssociations.getAssociationsForTransaction(
                  primaryId,
              )
            : await this.deps.storage.modules.matchingAssociations.getAssociationsForDocument(
                  primaryId,
              )

        // Filter candidates by date window
        const primaryDate = isTransactionPrimary
            ? new Date((primaryItem as BankTransaction).date)
            : new Date((primaryItem as Invoice).metadata.date || 0)

        const msPerDay = 86400000
        const lookback = new Date(
            primaryDate.getTime() - MATCHING_CONFIG.MAX_DAYS_WINDOW * msPerDay,
        )
        const lookforward = new Date(
            primaryDate.getTime() + MATCHING_CONFIG.MAX_DAYS_WINDOW * msPerDay,
        )

        const filteredCandidates = candidates.filter((candidate) => {
            const candidateDate = isTransactionPrimary
                ? new Date((candidate as Invoice).metadata.date || 0)
                : new Date((candidate as BankTransaction).date)
            return candidateDate >= lookback && candidateDate <= lookforward
        })

        const suggestions: DocumentTransactionAssociation[] = []

        for (const candidate of filteredCandidates) {
            if (isTransactionPrimary) {
                const tx = primaryItem as BankTransaction
                const inv = candidate as Invoice
                const { confidence } = this.computeMatchScore(tx, inv)

                if (
                    confidence >= MATCHING_CONFIG.MIN_CONFIDENCE_FOR_SUGGESTION
                ) {
                    const targetId = inv.id
                    const assoc = await this.createMatchSuggestion(
                        targetId,
                        primaryId,
                        confidence,
                    )
                    suggestions.push(assoc)
                }
            } else {
                const inv = primaryItem as Invoice
                const tx = candidate as BankTransaction
                const { confidence } = this.computeMatchScore(tx, inv) // note order, but function takes tx first

                if (
                    confidence >= MATCHING_CONFIG.MIN_CONFIDENCE_FOR_SUGGESTION
                ) {
                    const targetId = tx.id
                    const assoc = await this.createMatchSuggestion(
                        primaryId,
                        targetId,
                        confidence,
                    )
                    suggestions.push(assoc)
                }
            }
        }

        const sortedSuggestions = suggestions
            .sort((a, b) => b.confidence - a.confidence)
            .slice(0, MATCHING_CONFIG.MAX_SUGGESTIONS_PER_ITEM)

        // Check for auto-match threshold and auto-confirm top match if above threshold
        const settings = await this.deps.getServices().cache.getSettings()
        const autoMatchThreshold = settings?.autoMatchThreshold

        if (
            autoMatchThreshold !== null &&
            autoMatchThreshold !== undefined &&
            sortedSuggestions.length > 0
        ) {
            const topSuggestion = sortedSuggestions[0]
            if (
                topSuggestion.confidence >= autoMatchThreshold &&
                (!isTransactionPrimary ||
                    !existingAssocs.some(
                        (assoc) => assoc.method === 'user_confirmed',
                    ))
            ) {
                // Auto-confirm the top match
                try {
                    await this.confirmAssociation(
                        topSuggestion.documentId,
                        topSuggestion.transactionId,
                    )
                } catch (error) {
                    // Log error but don't fail the entire matching process
                    this.deps
                        .getServices()
                        .errorTracker.trackError(
                            'Failed to auto-confirm match',
                            error,
                            {
                                documentId: topSuggestion.documentId,
                                transactionId: topSuggestion.transactionId,
                                confidence: topSuggestion.confidence,
                                threshold: autoMatchThreshold,
                            },
                        )
                }
            }
        }

        return sortedSuggestions
    }

    async findMatchesForTransaction(
        transactionId: string,
        hardRefresh = false,
    ): Promise<DocumentTransactionAssociation[]> {
        return this._findMatches('transaction', transactionId, hardRefresh)
    }

    async findMatchesForInvoice(
        documentId: string,
        hardRefresh = false,
    ): Promise<DocumentTransactionAssociation[]> {
        return this._findMatches('invoice', documentId, hardRefresh)
    }

    async confirmAssociation(
        documentId: string,
        transactionId: string,
    ): Promise<DocumentTransactionAssociation> {
        const cache = this.deps.getServices().cache
        const existingAssociationIds =
            cache.getKey('matchesByInvoice')[documentId] || []

        const existingAssociations = existingAssociationIds.map(
            (id) => cache.getKey('documentTransactionAssociations')[id],
        )

        const existing = existingAssociations.find(
            (a) => a.transactionId === transactionId,
        )
        const timestamp = Date.now()

        if (existing) {
            const updatedAssoc: DocumentTransactionAssociation = {
                ...existing,
                method: 'user_confirmed',
            }
            await this.deps.storage.modules.matchingAssociations.updateAssociation(
                existing.id,
                {
                    method: 'user_confirmed',
                },
                timestamp,
            )
            if (cache) {
                await cache.setDocumentTransactionAssociations([updatedAssoc])
            }
            // Trigger category assignment (best-effort)
            try {
                await this.deps
                    .getServices()
                    .bankTransaction.assignCategoryAfterAssociation(
                        transactionId,
                    )
            } catch (e) {
                this.deps
                    .getServices()
                    .errorTracker.trackError(
                        'Failed to auto-assign category after association',
                        e,
                        { transactionId, documentId },
                    )
            }
            return updatedAssoc
        } else {
            const newAssoc =
                await this.deps.storage.modules.matchingAssociations.storeAssociation(
                    {
                        documentId,
                        transactionId,
                        confidence: 50,
                        method: 'user_confirmed',
                    },
                )
            if (cache) {
                await cache.setDocumentTransactionAssociations([newAssoc])
            }
            // Trigger category assignment (best-effort)
            try {
                await this.deps
                    .getServices()
                    .bankTransaction.assignCategoryAfterAssociation(
                        transactionId,
                    )
            } catch (e) {
                this.deps
                    .getServices()
                    .errorTracker.trackError(
                        'Failed to auto-assign category after association',
                        e,
                        { transactionId, documentId },
                    )
            }
            return {
                ...newAssoc,
            } as DocumentTransactionAssociation
        }
    }

    /**
     * Creates or updates a match suggestion (auto association) between a document and a transaction.
     *
     * @param documentId - The ID of the document to associate.
     * @param transactionId - The ID of the transaction to associate.
     * @param confidence - The confidence score for this auto match (0-100).
     * @returns The created or updated DocumentTransactionAssociation.
     */
    async createMatchSuggestion(
        documentId: string,
        transactionId: string,
        confidence: number,
    ): Promise<DocumentTransactionAssociation> {
        const associations =
            await this.deps.storage.modules.matchingAssociations.getAssociationsForDocument(
                documentId,
            )
        const cache = this.deps.getServices().cache
        const existing = associations.find(
            (a) => a.transactionId === transactionId,
        )

        const timestamp = Date.now()

        if (existing) {
            const updatedAssoc: DocumentTransactionAssociation = {
                ...existing,
                confidence,
            }
            await this.deps.storage.modules.matchingAssociations.updateAssociation(
                existing.id,
                updatedAssoc,
            )
            if (cache) {
                await cache.setDocumentTransactionAssociations([updatedAssoc])
            }
            return updatedAssoc
        } else {
            const newAssoc =
                await this.deps.storage.modules.matchingAssociations.storeAssociation(
                    {
                        documentId,
                        transactionId,
                        confidence,
                        method: 'auto',
                    },
                )
            if (cache) {
                await cache.setDocumentTransactionAssociations([newAssoc])
            }
            return {
                ...newAssoc,
            } as DocumentTransactionAssociation
        }
    }

    /**
     * Removes the association between a specific transaction and document (invoice).
     * If no association exists, does nothing.
     */
    async removeAssociation(
        transactionId: string,
        documentId: string,
    ): Promise<void> {
        const associations =
            await this.deps.storage.modules.matchingAssociations.getAssociationsForDocument(
                documentId,
            )

        const assoc = associations.find(
            (a) => a.transactionId === transactionId,
        )
        if (!assoc) {
            return
        }

        // Remove from storage
        await this.deps.storage.modules.matchingAssociations.deleteAssociation(
            assoc.id,
        )

        // Update cache
        const cache = this.deps.getServices().cache
        cache.removeDocumentTransactionAssociations([assoc.id])
    }

    async resetAssociationToAuto(
        transactionId: string,
        documentId: string,
    ): Promise<void> {
        const associationIds =
            this.deps.getServices().cache.getKey('matchesByInvoice')[
                documentId
            ] || []

        const associations = associationIds.map(
            (id) =>
                this.deps
                    .getServices()
                    .cache.getKey('documentTransactionAssociations')[id],
        )

        const associationToUpdate = associations.find(
            (a) => a.transactionId === transactionId,
        )

        if (!associationToUpdate) {
            throw new Error('Association not found')
            return
        }

        const now = Date.now()
        // Update in cache
        const updatedAssoc: DocumentTransactionAssociation = {
            ...associationToUpdate,
            method: 'auto',
            updatedAt: now,
        }
        const cache = this.deps.getServices().cache
        cache.setDocumentTransactionAssociations([updatedAssoc])

        // Update in storage
        try {
            await this.deps.storage.modules.matchingAssociations.updateAssociation(
                associationToUpdate.id,
                { method: 'auto' },
            )
        } catch (error) {
            const resetAssociation = {
                ...associationToUpdate,
                method: 'user_confirmed' as const,
                updatedAt: associationToUpdate.updatedAt,
            }
            await cache.setDocumentTransactionAssociations([resetAssociation])
            throw error
        }
    }

    /**
     * Refreshes all matching suggestions for the current user's transactions.
     * Clears existing associations and recomputes new ones based on current data.
     */
    async refreshAllMatchingSuggestions(): Promise<
        DocumentTransactionAssociation[]
    > {
        const userId = await this.getCurrentUserId()
        if (!userId) {
            throw new Error('User not authenticated')
        }

        const { cache } = this.deps.getServices()

        // Clear cache first
        const currentAssocs =
            cache.getKey('documentTransactionAssociations') || {}
        const currentIds = Object.keys(currentAssocs)
        if (currentIds.length > 0) {
            cache.removeDocumentTransactionAssociations(currentIds)
        }

        // Clear all associations from DB
        const allAssocs =
            await this.deps.storage.modules.matchingAssociations.getAllAssociations()
        const BATCH_SIZE = 100
        for (let i = 0; i < allAssocs.length; i += BATCH_SIZE) {
            const batch = allAssocs.slice(i, i + BATCH_SIZE)
            const promises = batch.map((assoc) =>
                this.deps.storage.modules.matchingAssociations.deleteAssociation(
                    assoc.id,
                ),
            )
            await Promise.all(promises)
        }

        const transactions = Object.values(
            cache.getKey('transactions') || {},
        ) as BankTransaction[]

        if (transactions.length === 0) {
            return []
        }

        const allSuggestions: DocumentTransactionAssociation[] = []

        for (const transaction of transactions) {
            // DB is now clear, so no need for hardRefresh
            const suggestions = await this.findMatchesForTransaction(
                transaction.id,
                false,
            )
            allSuggestions.push(...suggestions)
        }

        return allSuggestions
    }

    /**
     * Checks if there are any existing associations for the given invoice.
     */
    async hasAssociationsForInvoice(invoiceId: string): Promise<boolean> {
        const associations =
            await this.deps.storage.modules.matchingAssociations.getAssociationsForDocument(
                invoiceId,
            )
        return associations.length > 0
    }
}
